import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Navigate, Link, useNavigate } from 'react-router-dom';

import {
  fetchPlayerDashboard,
  fetchPlayerMatchesMeta,
  fetchPlayerMarkers,
  createVideoList,
} from './api';
import { useAsyncResource } from './hooks';
import PlayerIdentityCard from '../../components/PlayerIdentityCard';
import PlayerMatchesList from '../../components/PlayerMatchesList';
import ViewFullReportLink from '../../components/ViewFullReportLink';
import logo from '../../images/footerLogo.svg';
import PlayerLoader, { InlinePlayerLoader } from '../../components/PlayerLoader';
import { formatNumber } from '../../helpers/formatNumber';
import { retryAsync } from '../../helpers/retryAsync';
import { useLexicon } from '../../hooks/useLexicon';

// SMRTSTATS-312 #1: Home season + competition filter persistence. Survives
// nav-out / nav-back so the player doesn't have to re-pick every visit.
const SS_HOME_SEASON = 'player_home_season_id';
const SS_HOME_COMP_IDS = 'player_home_competition_ids';

const readJsonArray = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

// Trial banner is mounted globally in PlayerLayout; no inline banner here.

// Special pseudo-id for the synthesized "Matches played" tile (payload.matches_played
// lives at the root, not in the statistics array). Negative so it can't collide
// with a real parameter_id.
const MATCHES_PLAYED_KEY = -1;

const HEADLINE_PARAM_IDS = [MATCHES_PLAYED_KEY, 2, 201, 1, 122, 24, 4];
// param_id -> lexicon id (resolved via l() at render so labels translate).
const STAT_LABEL_OVERRIDE_IDS = {
  [MATCHES_PLAYED_KEY]: 325,
  1: 1812,
  2: 424,
  201: 105,
  122: 20,
  24: 18,
  4: 1813,
};

// Per-stat deep-link target. Tapping a tile navigates here. Stevan's rule
// (20.5.2026): every number leads to its underlying video / context.
//   - Matches played / Minutes → matches list (no event-level drilldown)
//   - Goals/Assists/Shots/etc with a small count → direct video player
//   - High-count event stats → events screen filtered to that parameter
const STAT_LINK_OVERRIDES = {
  [MATCHES_PLAYED_KEY]: '/player/matches',
  1: '/player/matches',
};

// Direct-to-video threshold. Above this we drop the user on the Events screen
// pre-filtered to the parameter so they can refine before watching. Below it
// we mint a watch token and skip straight to the video player — the
// "instant satisfaction" path the user asked for.
//
// Set to 600 (≈3 pages × 200/page) because backend caps marker pagination
// at 200 per page (verified on stage). With chunked-parallel fetching the
// wall-clock cost is roughly one page-time regardless of total count, so
// 600 keeps the perceived speed snappy even on flaky mobile networks.
const DIRECT_TO_VIDEO_MAX = 600;

// Backend page cap. Anything larger gets silently truncated, so we paginate.
const MARKER_PAGE_SIZE = 200;

const extractMarkerRows = (res) => {
  const body = res?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.results)) return body.results;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

// Fetch all marker rows for a parameter+season in parallel pages. For
// counts that fit in one backend page we issue a single request; otherwise
// we kick off N page requests in parallel so total wall time stays close
// to one page's response time even for 600-event tiles.
const fetchAllMarkers = async (mappedPlayerId, paramId, seasonId, expectedCount) => {
  const cap = Math.min(expectedCount, DIRECT_TO_VIDEO_MAX);
  const numPages = Math.max(1, Math.ceil(cap / MARKER_PAGE_SIZE));
  if (numPages === 1) {
    const res = await retryAsync(() => fetchPlayerMarkers(mappedPlayerId, paramId, {
      seasonId,
      perPage: MARKER_PAGE_SIZE,
    }));
    return extractMarkerRows(res);
  }
  const pageRequests = Array.from({ length: numPages }, (_, i) =>
    retryAsync(() => fetchPlayerMarkers(mappedPlayerId, paramId, {
      seasonId,
      perPage: MARKER_PAGE_SIZE,
      page: i + 1,
    })),
  );
  const results = await Promise.all(pageRequests);
  return results.flatMap(extractMarkerRows);
};

const PlayerDashboard = () => {
  const mappedPlayerId = useSelector((state) => state.user.mappedPlayerId);
  const navigate = useNavigate();
  const l = useLexicon();

  const [selectedSeasonId, setSelectedSeasonId] = useState(
    () => sessionStorage.getItem(SS_HOME_SEASON) || '',
  );
  const [selectedCompetitionIds, setSelectedCompetitionIds] = useState(
    () => new Set(readJsonArray(SS_HOME_COMP_IDS).map(String)),
  );

  const { data, loading, error } = useAsyncResource(
    () => fetchPlayerDashboard({
      seasonId: selectedSeasonId || undefined,
      competitionIds: Array.from(selectedCompetitionIds),
    }),
    // Re-fetch when either filter changes. competitionIds is a Set so we
    // join into a stable key for the dependency comparison.
    [selectedSeasonId, Array.from(selectedCompetitionIds).sort().join(',')],
  );

  useEffect(() => {
    if (selectedSeasonId) sessionStorage.setItem(SS_HOME_SEASON, selectedSeasonId);
    else sessionStorage.removeItem(SS_HOME_SEASON);
  }, [selectedSeasonId]);

  useEffect(() => {
    sessionStorage.setItem(
      SS_HOME_COMP_IDS,
      JSON.stringify(Array.from(selectedCompetitionIds)),
    );
  }, [selectedCompetitionIds]);

  // player.teams (drives W/L/D coloring) lives on the v1 statistics endpoint,
  // not on /player/dashboard. Pull it once on mount.
  const [playerTeams, setPlayerTeams] = useState([]);
  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    fetchPlayerMatchesMeta(mappedPlayerId)
      .then((res) => { if (!cancelled) setPlayerTeams(res.data?.player?.teams ?? []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mappedPlayerId]);

  // "Show all season stats" expander — collapsed by default so the screen
  // leads with the 7 primary KPIs (ticket SMRTSTATS-297, Home §5).
  const [moreStatsOpen, setMoreStatsOpen] = useState(false);

  // Per-tile loading state for the direct-to-video click chain. Only one
  // tile can be in flight at a time; clicks on others are ignored until the
  // current one resolves or errors.
  const [busyStatId, setBusyStatId] = useState(null);
  const [clickError, setClickError] = useState(null);

  if (!mappedPlayerId) {
    return <Navigate to="/player/not-mapped" replace />;
  }

  const payload = data?.data ?? data?.results ?? data;
  const yearSeason = payload?.year_season;
  const statistics = payload?.statistics ?? [];
  const recentMatches = payload?.recent_matches ?? [];
  const matchesPlayed = payload?.matches_played ?? 0;
  const player = payload?.player;
  const availableSeasons = Array.isArray(payload?.available_seasons)
    ? payload.available_seasons
    : [];
  const availableCompetitions = Array.isArray(payload?.available_competitions)
    ? payload.available_competitions
    : [];

  // Build the headline tiles. Matches played isn't in the statistics array
  // (lives at payload root), so synthesize a row for it under MATCHES_PLAYED_KEY.
  const statsById = new Map(statistics.map((s) => [s.parameter_id, s]));
  statsById.set(MATCHES_PLAYED_KEY, { parameter_id: MATCHES_PLAYED_KEY, count: matchesPlayed });
  const headlineStats = HEADLINE_PARAM_IDS
    .map((id) => statsById.get(id))
    .filter(Boolean);

  // Remaining stats for the expander — everything fetched that isn't already
  // in the primary tiles. Order preserved from the API response.
  const headlineIdSet = new Set(HEADLINE_PARAM_IDS);
  const extraStats = statistics.filter((s) => !headlineIdSet.has(s.parameter_id));

  const handleStatClick = async (stat) => {
    if (busyStatId != null) return;
    const paramId = stat.parameter_id;
    const count = Number(stat.count);

    // Override-mapped tiles (Matches / Minutes) have no per-event meaning —
    // just navigate to the matches list.
    if (STAT_LINK_OVERRIDES[paramId]) {
      navigate(STAT_LINK_OVERRIDES[paramId]);
      return;
    }

    const eventsFallback = () => navigate(`/player/events?param=${paramId}`);

    // Over-threshold or zero-count → no direct video. Route through Events
    // so the user can refine (or sees an empty list with the right filter
    // applied for context).
    if (!Number.isFinite(count) || count <= 0 || count > DIRECT_TO_VIDEO_MAX) {
      eventsFallback();
      return;
    }

    setBusyStatId(paramId);
    setClickError(null);
    try {
      const rows = await fetchAllMarkers(mappedPlayerId, paramId, yearSeason?.id, count);
      const markerIds = Array.from(new Set(
        rows.map((r) => Number(r?.id)).filter(Number.isFinite),
      ));
      if (markerIds.length === 0) {
        eventsFallback();
        return;
      }
      const label = STAT_LABEL_OVERRIDE_IDS[paramId]
        ? l(STAT_LABEL_OVERRIDE_IDS[paramId])
        : (stat.parameter_name ?? l(9));
      const fullName = [player?.name, player?.surname].filter(Boolean).join(' ') || l(1800);
      const listRes = await retryAsync(
        () => createVideoList(markerIds, `${label} — ${fullName}`),
      );
      const token = listRes.data?.token;
      if (!token) {
        eventsFallback();
        return;
      }
      navigate(`/player/events/video?token=${token}`);
    } catch (e) {
      setClickError(l(1820));
      eventsFallback();
    } finally {
      setBusyStatId(null);
    }
  };

  return (
    <Wrap>
      <BrandBar>
        <BrandLogo src={logo} alt="SmrtStats" />
      </BrandBar>

      {loading && <PlayerLoader label={l(1809)} />}
      {error && <ErrorBox>{l(1810)}</ErrorBox>}

      {!loading && !error && payload && (
        <>
          {player && <PlayerIdentityCard player={player} />}

          {availableSeasons.length > 1 && (
            <FilterRow>
              <FilterLabel>{l(453)}</FilterLabel>
              <FilterSelect
                value={selectedSeasonId || yearSeason?.id || ''}
                onChange={(e) => {
                  setSelectedSeasonId(e.target.value);
                  // Comp IDs are season-scoped; reset on season change so a
                  // stale id from the previous season doesn't filter the
                  // tiles to empty.
                  setSelectedCompetitionIds(new Set());
                }}
              >
                {availableSeasons.map((s) => (
                  <option key={s.id} value={s.id}>{s.years ?? s.name ?? s.id}</option>
                ))}
              </FilterSelect>
            </FilterRow>
          )}

          {availableCompetitions.length > 1 && (
            <FilterRow>
              <FilterLabel>{l(454)}</FilterLabel>
              <FilterSelect
                value={
                  selectedCompetitionIds.size === 1
                    ? Array.from(selectedCompetitionIds)[0]
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedCompetitionIds(v ? new Set([v]) : new Set());
                }}
              >
                <option value="">{l(1704)}</option>
                {availableCompetitions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </FilterSelect>
            </FilterRow>
          )}

          <SeasonStrip>
            <SeasonLabel>{yearSeason?.years ?? l(1811)}</SeasonLabel>
            <MatchesPill>{formatNumber(matchesPlayed)} {l(1699)}</MatchesPill>
          </SeasonStrip>

          <StatsGrid>
            {headlineStats.map((stat) => {
              const isBusy = busyStatId === stat.parameter_id;
              const label = STAT_LABEL_OVERRIDE_IDS[stat.parameter_id]
                ? l(STAT_LABEL_OVERRIDE_IDS[stat.parameter_id])
                : stat.parameter_name;
              return (
                <StatCard
                  key={stat.parameter_id}
                  type="button"
                  onClick={() => handleStatClick(stat)}
                  disabled={busyStatId != null && !isBusy}
                  aria-label={`${label}: ${formatNumber(stat.count)} — ${l(1819)}`}
                  aria-busy={isBusy || undefined}
                >
                  <StatValue>
                    {isBusy ? <InlinePlayerLoader /> : formatNumber(stat.count)}
                  </StatValue>
                  <StatLabel>{label}</StatLabel>
                </StatCard>
              );
            })}
          </StatsGrid>
          {clickError && <ClickError role="status">{clickError}</ClickError>}

          {extraStats.length > 0 && (
            <MoreStats>
              <MoreStatsToggle
                type="button"
                onClick={() => setMoreStatsOpen((v) => !v)}
                aria-expanded={moreStatsOpen}
              >
                <span>{moreStatsOpen ? l(1814) : l(1815)}</span>
                <Chevron $open={moreStatsOpen}>›</Chevron>
              </MoreStatsToggle>
              {moreStatsOpen && (
                <StatsGrid>
                  {extraStats.map((stat) => {
                    const isBusy = busyStatId === stat.parameter_id;
                    return (
                      <StatCard
                        key={stat.parameter_id}
                        type="button"
                        onClick={() => handleStatClick(stat)}
                        disabled={busyStatId != null && !isBusy}
                        aria-label={`${stat.parameter_name}: ${formatNumber(stat.count)} — ${l(1819)}`}
                        aria-busy={isBusy || undefined}
                      >
                        <StatValue>
                          {isBusy ? <InlinePlayerLoader /> : formatNumber(stat.count)}
                        </StatValue>
                        <StatLabel>{stat.parameter_name}</StatLabel>
                      </StatCard>
                    );
                  })}
                </StatsGrid>
              )}
            </MoreStats>
          )}

          <SectionHeader>
            <SectionTitle>{l(1816)}</SectionTitle>
            <SectionLink to="/player/matches">{l(1817)}</SectionLink>
          </SectionHeader>

          {recentMatches.length === 0 ? (
            <Muted>{l(1818)}</Muted>
          ) : (
            <PlayerMatchesList matches={recentMatches} playerTeams={playerTeams} />
          )}

          <ViewFullReportLink seasonId={yearSeason?.id} />
        </>
      )}
    </Wrap>
  );
};

export default PlayerDashboard;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BrandBar = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;
`;

const BrandLogo = styled.img`
  width: 120px;
  display: block;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterLabel = styled.label`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  width: 88px;
  flex-shrink: 0;
`;

const FilterSelect = styled.select`
  flex: 1;
  min-width: 0;
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 10px;
  padding: 12px 36px 12px 14px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, ${(props) => props.theme.blueish2} 50%),
                    linear-gradient(135deg, ${(props) => props.theme.blueish2} 50%, transparent 50%);
  background-position: calc(100% - 18px) 50%, calc(100% - 13px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  cursor: pointer;
  text-overflow: ellipsis;

  &:focus { outline: 2px solid ${(props) => props.theme.green}; outline-offset: 2px; }
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const ClickError = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
  font-family: 'SatoshiMedium', sans-serif;
  padding: 2px 4px;
`;

const SeasonStrip = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${(props) => props.theme.row};
  border-radius: 12px;
`;

const SeasonLabel = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
`;

const MatchesPill = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const StatCard = styled.button`
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover { border-color: ${(props) => props.theme.green}; }
  &:focus-visible { outline: 2px solid ${(props) => props.theme.green}; outline-offset: 2px; }
`;

const MoreStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MoreStatsToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover { border-color: ${(props) => props.theme.blueish2}; }
`;

const Chevron = styled.span`
  font-size: 18px;
  line-height: 1;
  color: ${(props) => props.theme.blueish2};
  transform: rotate(${(props) => (props.$open ? '90deg' : '0')});
  transition: transform 160ms ease;
`;

const StatValue = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 8px;
`;

const SectionTitle = styled.h2`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 16px;
  margin: 0;
`;

const SectionLink = styled(Link)`
  font-size: 13px;
  color: ${(props) => props.theme.green};
  text-decoration: none;
`;

